import {
  checkValidCNPJ,
  formatCpfOrCnpj,
  formatOnlyAlphanumeric,
  formatOnlyNumbers,
} from '@interco/lib-util'

type PixDocumentType = 'CPF' | 'CNPJ'

type FormatPixDocumentKeyParams = {
  key?: string
  keyType: PixDocumentType
}

const CPF_LENGTH = 11
const CNPJ_LENGTH = 14

const hasAllEqualDigits = (value: string) => /^(\d)\1+$/.test(value)

const getCpfCheckDigit = (cpfSlice: string) => {
  const weightStart = cpfSlice.length + 1
  const sum = cpfSlice
    .split('')
    .reduce(
      (accumulator, currentDigit, index) =>
        accumulator + Number(currentDigit) * (weightStart - index),
      0,
    )

  const remainder = (sum * 10) % 11

  return remainder === 10 ? 0 : remainder
}

export const isValidCpf = (value?: string) => {
  if (!value) {
    return false
  }

  const onlyNumbers = formatOnlyNumbers(value)

  if (onlyNumbers.length !== CPF_LENGTH || hasAllEqualDigits(onlyNumbers)) {
    return false
  }

  const firstDigit = getCpfCheckDigit(onlyNumbers.slice(0, 9))
  const secondDigit = getCpfCheckDigit(onlyNumbers.slice(0, 10))

  return `${firstDigit}${secondDigit}` === onlyNumbers.slice(9)
}

export const isValidCnpj = (value?: string) => {
  if (!value) {
    return false
  }

  const sanitizedCnpj = formatOnlyAlphanumeric(value).toUpperCase()

  if (sanitizedCnpj.length !== CNPJ_LENGTH) {
    return false
  }

  return checkValidCNPJ(sanitizedCnpj)
}

export const formatPixDocumentKey = ({ key, keyType }: FormatPixDocumentKeyParams) => {
  if (!key) {
    return ''
  }

  const shouldFormatCpf = keyType === 'CPF' && isValidCpf(key)
  const shouldFormatCnpj = keyType === 'CNPJ' && isValidCnpj(key)

  if (!shouldFormatCpf && !shouldFormatCnpj) {
    return key
  }

  return (
    formatCpfOrCnpj({
      value: key.toUpperCase(),
    }) || key
  )
}
